import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import {format} from 'date-fns';
import {useData} from '../contexts/DataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiUser,FiClock,FiTag,FiArrowRight,FiEdit,FiMail}=FiIcons;

const NoteCard=({note,showMember=false})=> {
  const {members}=useData();

  const getCategoryColor=(category)=> {
    const colors={
      'Performance': 'bg-green-100 text-green-800',
      'Areas for Improvement': 'bg-orange-100 text-orange-800',
      'Mindset': 'bg-blue-100 text-blue-800',
      'Strategy': 'bg-purple-100 text-purple-800',
      'Marketing': 'bg-indigo-100 text-indigo-800',
      'Technical': 'bg-yellow-100 text-yellow-800',
      'General': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors['General'];
  };

  const member=showMember ? members.find(m=> m.id===note.member_id) : null;

  // Format content to preserve line breaks and spacing
  const formatContent=(content)=> {
    if (!content) return '';
    
    // Split content by line breaks and map to JSX
    return content.split('\n').map((line,index,array)=> (
      <React.Fragment key={index}>
        {line}
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <motion.div
      initial={{opacity: 0,y: 20}}
      animate={{opacity: 1,y: 0}}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
    >
      {showMember && member && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiUser} className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <Link
                to={`/members/${member.id}`}
                className="text-lg font-semibold text-gray-900 hover:text-primary-600"
              >
                {member.name}
              </Link>
              <p className="text-sm text-gray-500 flex items-center">
                <SafeIcon icon={FiMail} className="w-3 h-3 mr-1" />
                <a
                  href={`mailto:${member.email}`}
                  className="hover:text-primary-600 transition-colors"
                >
                  {member.email}
                </a>
              </p>
              <Link
                to={`/members/${member.id}`}
                className="md:hidden text-primary-600 hover:text-primary-700 flex items-center text-sm font-medium mt-1"
              >
                View Profile
                <SafeIcon icon={FiArrowRight} className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
          <Link
            to={`/members/${member.id}`}
            className="hidden md:flex text-primary-600 hover:text-primary-700 items-center text-sm font-medium"
          >
            View Profile
            <SafeIcon icon={FiArrowRight} className="ml-1 w-4 h-4" />
          </Link>
        </div>
      )}

      <div className="flex items-start justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900">{note.title}</h3>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(note.category)}`}
        >
          <SafeIcon icon={FiTag} className="w-3 h-3 inline mr-1" />
          {note.category}
        </span>
      </div>

      {/* Preserve formatting in content display */}
      <div className="text-gray-700 mb-4 leading-relaxed whitespace-pre-wrap">
        {formatContent(note.content)}
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between text-sm text-gray-500">
        <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiUser} className="w-4 h-4" />
            <span>{note.coach_name}</span>
          </div>
          <div className="flex items-center space-x-1 mt-1 md:mt-0">
            <SafeIcon icon={FiClock} className="w-4 h-4" />
            <span>{format(new Date(note.timestamp),'MMM d,yyyy h:mm a')}</span>
          </div>
        </div>
        {/* Edit link */}
        <Link
          to={`/edit-note/${note.id}/${note.member_id}`}
          className="flex items-center text-primary-600 hover:text-primary-700 mt-2 md:mt-0"
        >
          <SafeIcon icon={FiEdit} className="w-4 h-4 mr-1" />
          <span>Edit</span>
        </Link>
      </div>
    </motion.div>
  );
};

export default NoteCard;